// src/components/MedicinesPage/MedicationListCard/MedicationListCardDoctor.jsx
"use client";

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip, notification } from "antd";
import {
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
  SyncOutlined,
  FieldTimeOutlined,
  EditOutlined,
  PlusOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import {
  archiveMedication,
  fetchMedications,
} from "../../redux/thunks/medicationThunks";
import {
  selectMedicationLoading,
  selectActiveMedications,
} from "../../redux/slices/medicationSlice";
import { fetchUserById } from "../../../Profile/redux/thunks/userThunks";
import AddModal from "../AddModal/AddModal";

import "./MedicationListCard.css";
import "./MedicationListCardDoctor.css";

const PillIcon = ({ className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    stroke="currentColor"
    fill="none"
    strokeWidth="1.5"
    aria-hidden
  >
    <g transform="rotate(-28 12 12)">
      <rect x="5" y="7" width="14" height="10" rx="5" />
      <line x1="12" y1="7" x2="12" y2="17" strokeLinecap="round" />
    </g>
  </svg>
);

const TrackingMode = {
  DAILY: "daily",
  SPECIFIC_DAYS: "specificDays",
  LIFETIME: "lifetime",
};

const DAYS_SHORT_LABELS = {
  0: "Dim",
  1: "Lun",
  2: "Mar",
  3: "Mer",
  4: "Jeu",
  5: "Ven",
  6: "Sam",
};

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const projectTimeToDate = (selectedDate, templateTime) => {
  const sd = new Date(selectedDate);
  const tt = new Date(templateTime);
  if (Number.isNaN(sd.getTime()) || Number.isNaN(tt.getTime())) return null;
  const out = new Date(sd);
  out.setHours(tt.getHours(), tt.getMinutes(), tt.getSeconds(), 0);
  return out;
};

const getTrackingMode = (med) => med?.tracking?.mode || TrackingMode.DAILY;
const getSelectedDays = (med) => {
  const days = med?.tracking?.selectedDays;
  return Array.isArray(days) ? days : [];
};

const isTakingDay = (med, date) => {
  const mode = getTrackingMode(med);
  const d = new Date(date);
  switch (mode) {
    case TrackingMode.DAILY:
    case TrackingMode.LIFETIME:
      return true;
    case TrackingMode.SPECIFIC_DAYS: {
      const dayOfWeek = d.getDay();
      return getSelectedDays(med).includes(dayOfWeek);
    }
    default:
      return true;
  }
};

const isInDateRange = (med, date) => {
  const sel0 = startOfDay(date).getTime();
  const s = med.startDate ? startOfDay(med.startDate).getTime() : null;
  if (s != null && sel0 < s) return false;
  const mode = getTrackingMode(med);
  if (mode === TrackingMode.LIFETIME) return s == null || sel0 >= s;
  const e = med.endDate ? startOfDay(med.endDate).getTime() : null;
  if (s == null && e == null) return true;
  if (s == null) return sel0 <= e;
  if (e == null) return sel0 >= s;
  return sel0 >= s && sel0 <= e;
};

const getTrackingLabel = (med, t) => {
  const mode = getTrackingMode(med);
  switch (mode) {
    case TrackingMode.DAILY:
      return t("medicationListCard.tracking.daily", { defaultValue: "Tous les jours" });
    case TrackingMode.LIFETIME:
      return t("medicationListCard.tracking.lifetime", { defaultValue: "À vie" });
    case TrackingMode.SPECIFIC_DAYS: {
      const selectedDays = getSelectedDays(med);
      const order = [1, 2, 3, 4, 5, 6, 0];
      const dayLabels = [...selectedDays]
        .sort((a, b) => order.indexOf(a) - order.indexOf(b))
        .map((d) => DAYS_SHORT_LABELS[d] || "")
        .filter(Boolean)
        .join(", ");
      return dayLabels || t("medicationListCard.tracking.specificDays", { defaultValue: "Jours spécifiques" });
    }
    default:
      return "";
  }
};

const getTrackingIcon = (med) => {
  const mode = getTrackingMode(med);
  switch (mode) {
    case TrackingMode.DAILY:
      return <CalendarOutlined />;
    case TrackingMode.SPECIFIC_DAYS:
      return <SyncOutlined />;
    case TrackingMode.LIFETIME:
      return <FieldTimeOutlined />;
    default:
      return <CalendarOutlined />;
  }
};

const toHHMM = (dateLike) => {
  try {
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return "--:--";
    return d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Africa/Tunis",
    });
  } catch {
    return "--:--";
  }
};

export default function MedicationListCardDoctor({
  patientId,
  title,
  date = new Date(),
  canCreate = false,
  canUpdate = false,
  canStop = false,
  onAuthorizationFailure,
}) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const userId = patientId || null;
  const meds = useSelector(selectActiveMedications);
  const loading = useSelector(selectMedicationLoading);
  const [openModal, setOpenModal] = useState(false);
  const [editingMedication, setEditingMedication] = useState(null);

  useEffect(() => {
    if (!userId) return;
    dispatch(fetchUserById(userId));
    dispatch(fetchMedications({ userId }));
  }, [dispatch, userId]);

  const locale = useMemo(() => {
    const lng = (i18n.language || "en").toLowerCase();
    if (lng.startsWith("ar")) return "ar-TN";
    if (lng.startsWith("fr")) return "fr-FR";
    return "en-US";
  }, [i18n.language]);

  const fmtWeekdayShort = useCallback(
    (d) => d.toLocaleDateString(locale, { weekday: "short" }),
    [locale]
  );
  const fmtWeekdayLower = useCallback(
    (d) => String(d.toLocaleDateString(locale, { weekday: "long" })).toLowerCase(),
    [locale]
  );
  const fmtMonthLong = useCallback(
    (d) => d.toLocaleDateString(locale, { month: "long" }),
    [locale]
  );
  const fmtDay = useCallback((d) => d.getDate(), []);
  const fmtYear = useCallback((d) => d.getFullYear(), []);

  const [cursor, setCursor] = useState(0);
  const [selected, setSelected] = useState(startOfDay(date));

  const days = useMemo(() => {
    const base = startOfDay(selected);
    const out = [];
    for (let i = -3; i <= 3; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      out.push(d);
    }
    return out;
  }, [selected]);

  const centerIdx = 3;
  const daysWithMeds = useMemo(() => {
    if (!Array.isArray(meds)) return new Set();
    const result = new Set();
    days.forEach((day, idx) => {
      const hasMed = meds.some((m) => isInDateRange(m, day) && isTakingDay(m, day));
      if (hasMed) result.add(idx);
    });
    return result;
  }, [meds, days]);

  const mappedMeds = useMemo(() => {
    if (!Array.isArray(meds)) return [];

    return [...meds]
      .sort((a, b) => {
        const ta = new Date(a.createdAt || a.updatedAt || 0).getTime();
        const tb = new Date(b.createdAt || b.updatedAt || 0).getTime();
        return tb - ta;
      })
      .filter((m) => !m.isArchived && isInDateRange(m, selected) && isTakingDay(m, selected))
      .map((m) => {
        const doseLabelKey = (m.amount ?? 1) > 1 ? "medsDoctor.dosesPerDay" : "medsDoctor.dosePerDay";
        const subtitle = `${m.amount ?? 1} ${t(doseLabelKey)}`;

        const times = Array.isArray(m.times) ? m.times : [];
        const timePills = times
          .map((tt, idx) => ({
            compositeId: `${m.id || m._id}:${idx}:${startOfDay(selected).toISOString()}`,
            index: idx,
            label: toHHMM(projectTimeToDate(selected, tt)),
            rawTime: tt,
            taken: Array.isArray(m.takenPerDose)
              ? Boolean(m.takenPerDose[idx])
              : Boolean(m.isTaken),
          }))
          .sort((a, b) => new Date(a.rawTime) - new Date(b.rawTime));

        return {
          medId: m.id || m._id,
          name: m.name,
          subtitle,
          notes: m.notes || "",
          trackingLabel: getTrackingLabel(m, t),
          trackingIcon: getTrackingIcon(m),
          timePills,
          version: m.version,
          source: m.source,
          prescribedByDoctorId: m.prescribedByDoctorId,
          createdByActorId: m.createdByActorId,
          raw: m,
        };
      });
  }, [meds, selected, t]);

  const handleDisabledClick = useCallback(() => {
    notification.info({ message: t("medsDoctor.doctorReadOnlyInfo") });
  }, [t]);

  const handleStop = useCallback(
    async (medication) => {
      if (!canStop) {
        handleDisabledClick();
        return;
      }
      const result = await dispatch(
        archiveMedication({
          userId,
          id: medication.medId,
          version: medication.version,
        }),
      );
      if (archiveMedication.rejected.match(result)) {
        notification.error({ message: result.payload || "Arrêt du traitement refusé" });
        onAuthorizationFailure?.();
        return;
      }
      dispatch(fetchMedications({ userId }));
    },
    [
      canStop,
      dispatch,
      handleDisabledClick,
      onAuthorizationFailure,
      userId,
    ],
  );

  const closeMedicationModal = useCallback(() => {
    setOpenModal(false);
    setEditingMedication(null);
  }, []);

  const finalTitle = title || t("meds.title");

  const dateLine = t("medsDoctor.listForDate", {
    month: fmtMonthLong(selected),
    weekday: fmtWeekdayLower(selected),
    day: fmtDay(selected),
    year: fmtYear(selected),
  });

  return (
    <div className="medicationlistcarddoctor mlc-card">
      <div className="mlcd-head mlc-head">
        <h3 className="mlc-title">{finalTitle}</h3>
        {canCreate && (
          <Tooltip title="Créer un traitement autorisé par le patient">
            <button
              className="mlc-add-btn"
              onClick={() => {
                setEditingMedication(null);
                setOpenModal(true);
              }}
              type="button"
            >
              <PlusOutlined />
              <span>{t("prescriptionCard.ui.addMedication")}</span>
            </button>
          </Tooltip>
        )}
      </div>

      <div className="mlc-date-row">
        <div className="mlc-date-left">{dateLine}</div>
        <Tooltip title={t("medsDoctor.doctorReadOnlyInfo")}>
          <button
            className="mlc-icon-btn mlc-icon-btn--doctor"
            onClick={handleDisabledClick}
            type="button"
            aria-label={t("medsDoctor.readOnly")}
          />
        </Tooltip>
      </div>

      <div className="mlc-doctor-note">{t("medsDoctor.doctorReadOnlyInfo")}</div>

      <div className="mlc-days">
        <button
          className="mlc-nav"
          onClick={() => setSelected((prev) => {
            const d = new Date(prev);
            d.setDate(d.getDate() - 1);
            return startOfDay(d);
          })}
          aria-label={t("medsDoctor.previousDays")}
          type="button"
        >
          <LeftOutlined />
        </button>

        <div className="mlc-day-pills" role="tablist" aria-label={t("medsDoctor.days")}>
          {days.map((d, idx) => {
            const isSel = idx === centerIdx;
            const hasMeds = daysWithMeds.has(idx);
            return (
              <button
                key={d.toDateString() + idx}
                className={`mlc-day-pill ${isSel ? "is-active" : ""} ${hasMeds ? "has-meds" : ""}`}
                onClick={() => setSelected(startOfDay(d))}
                type="button"
              >
                <div className="mlc-day-num">{fmtDay(d)}</div>
                <div className={`mlc-day-dow ${isSel ? "is-active" : ""}`}>{fmtWeekdayShort(d)}</div>
                {hasMeds && <span className="mlc-day-dot" aria-hidden />}
              </button>
            );
          })}
        </div>

        <button
          className="mlc-nav"
          onClick={() => setSelected((prev) => {
            const d = new Date(prev);
            d.setDate(d.getDate() + 1);
            return startOfDay(d);
          })}
          aria-label={t("medsDoctor.nextDays")}
          type="button"
        >
          <RightOutlined />
        </button>
      </div>

      <div className="mlc-list">
        {loading && mappedMeds.length === 0 ? (
          <div className="mlc-empty">{t("medsDoctor.loading")}</div>
        ) : mappedMeds.length === 0 ? (
          <div className="mlc-empty">{t("medsDoctor.empty")}</div>
        ) : (
          mappedMeds.map((med) => (
            <div key={med.medId} className="mlc-item">
              <div className="mlc-time">
                <div className="mlc-time-pills">
                  {med.timePills.map((tp) => (
                    <button
                      key={tp.compositeId}
                      className={`mlc-time-pill ${tp.taken ? "is-taken" : ""}`}
                      onClick={handleDisabledClick}
                      type="button"
                      aria-pressed={tp.taken}
                      title={t("medsDoctor.readOnly")}
                    >
                      {tp.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mlc-line" aria-hidden />

              <div className="mlc-main">
                <div className="mlc-header-line">
                  <div className="mlc-name">{med.name}</div>
                  <div className="mlc-notes">
                    {t("medsDoctor.notes")} {med.notes || "—"}
                  </div>
                  {canUpdate && (
                    <Tooltip title="Modifier ce traitement">
                      <button
                        className="mlc-icon-btn"
                        type="button"
                        onClick={() => {
                          setEditingMedication(med.raw);
                          setOpenModal(true);
                        }}
                      >
                        <EditOutlined />
                      </button>
                    </Tooltip>
                  )}
                </div>

                <div className="mlc-sub">
                  <span className="mlc-pill-ic" aria-hidden>
                    <PillIcon />
                  </span>
                  <span>{med.subtitle}</span>
                  {med.trackingLabel ? <span className="mlc-tracking-label">• {med.trackingLabel}</span> : null}
                </div>
              </div>

              <button
                className="mlc-toggle"
                onClick={() => handleStop(med)}
                type="button"
                disabled={!canStop}
                title={
                  canStop
                    ? "Arrêter le traitement"
                    : t("medsDoctor.doctorReadOnlyInfo")
                }
              >
                <span className="mlc-status">
                  {canStop
                    ? "Arrêter"
                    : t("medsDoctor.readOnly")}
                </span>
                {canStop ? (
                  <StopOutlined />
                ) : (
                  <span className="mlc-dot" aria-hidden />
                )}
              </button>
            </div>
          ))
        )}
      </div>

      <AddModal
        open={openModal}
        onClose={closeMedicationModal}
        onCreateMedication={() => {
          closeMedicationModal();
          dispatch(fetchMedications({ userId }));
        }}
        userId={userId}
        doctorMode
        editMedication={editingMedication}
      />
    </div>
  );
}
